import type { FormatId } from "./format.js";
import type { LocaleCode } from "../locales/index.js";

export type { FormatId, LocaleCode };

export const FPS = 30;

/**
 * A Hyperframes Fragment is the unit a primitive emits. It combines:
 *  - HTML to insert into the composition body
 *  - optional <style> CSS scoped via element IDs/classes
 *  - an `init` step (gsap.set or assignments) that runs once before the timeline plays
 *  - timeline tweens, expressed as raw GSAP method calls that get appended to the
 *    project-wide paused timeline (`tl.from(...) / tl.to(...) / tl.set(...)`).
 *
 * Tweens use absolute positions (third arg to gsap calls) so we never depend on
 * the previous fragment's duration — every primitive owns its own timing.
 */
export type Fragment = {
  html: string;
  css?: string;
  init?: string;
  tweens?: string[];
};

export type BuildContext = {
  format: FormatId;
  locale: LocaleCode;
  durationSec: number;
  width: number;
  height: number;
  /** Render-Theme. Schaltet die Palette (bg/fg/muted) um; Akzente bleiben. */
  theme?: "dark" | "light";
  /** Brand assets URL prefix — empty when assets are colocated with index.html. */
  assetBase?: string;
};

export function mergeFragments(parts: Fragment[]): Fragment {
  return {
    html: parts.map((p) => p.html).join("\n"),
    css: parts
      .map((p) => p.css)
      .filter((c): c is string => Boolean(c))
      .join("\n"),
    init: parts
      .map((p) => p.init)
      .filter((s): s is string => Boolean(s))
      .join("\n"),
    tweens: parts.flatMap((p) => p.tweens ?? []),
  };
}
